import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PersonEditor, EMPTY_PERSON, editable } from "./PersonEditor";
import { DeletedAccounts, DeletionJobs } from "./DeleteAccount";
import { MemberRow } from "./MemberRow";
import { SelfGuests } from "./SelfGuests";
import { GuestSignupCard } from "./GuestSignupCard";

const FILTERS = [["all", "All"], ["family", "Family"], ["guests", "Guests"], ["suspicious", "Suspicious"], ["pending", "Pending"], ["unverified", "Unverified"], ["blocked", "Blocked"], ["birthdays", "Birthdays"]];

// one row per human: account + linked People entry (auto-linked by email on the server)
const mergeRows = (users, people) => {
  const byUser = Object.fromEntries(people.filter((p) => p.user_id).map((p) => [p.user_id, p]));
  const ids = new Set(users.map((u) => u.user_id));
  return [
    ...users.map((u) => ({ key: u.user_id, user: u, person: byUser[u.user_id] || null })),
    ...people.filter((p) => !p.user_id || !ids.has(p.user_id)).map((p) => ({ key: p.id, user: null, person: p })),
  ];
};

const matches = (r, f) => {
  const u = r.user;
  const p = r.person;
  if (f === "family") return u && !u.blocked && ["family", "admin"].includes(u.role);
  if (f === "guests") return !!p?.guest;
  if (f === "pending") return u && u.role === "pending" && !u.blocked && u.email_verified;
  if (f === "unverified") return u && !u.email_verified;
  if (f === "blocked") return u?.blocked;
  if (f === "birthdays") return !!p?.month;
  return true;
};

export const MembersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [people, setPeople] = useState([]);
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(FILTERS.some(([k]) => k === params.get("filter")) ? params.get("filter") : "all");
  const [suspicious, setSuspicious] = useState(0);
  useEffect(() => { api.get("/admin/guest-signups").then((r) => setSuspicious(r.data.suspicious)); }, []);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => Promise.all([api.get("/admin/users"), api.get("/people")]).then(([u, p]) => { setUsers(u.data.users); setPeople(p.data.people); }), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const ops = {
    patchUser: (u, body) => run(() => api.patch(`/admin/users/${u.user_id}`, body), "Updated"),
    sendReset: async (u) => {
      if (!window.confirm(`Email ${u.email} a password reset link?`)) return;
      try {
        const { data: r } = await api.post(`/admin/users/${u.user_id}/send-reset`);
        toast.success(r.kind === "google" ? "Sent: this account signs in with Google, so we emailed how to sign in" : "Reset email sent");
      } catch (e) { toast.error(errMsg(e)); }
    },
    person: (kind, p) => {
      if (kind === "email") return run(() => api.post(`/people/${p.id}/guest/email`), "Link emailed");
      if (kind === "grant") {
        const g = p.guest || {};
        return run(() => api.post(`/people/${p.id}/guest`, { days: 7, username: g.username || null, album_mode: g.album_mode || "all", album_ids: g.album_ids || [] }), "Guest access ready for 7 days");
      }
      if (kind === "revoke") return window.confirm(`Revoke ${p.name}'s guest access?`) && run(() => api.delete(`/people/${p.id}/guest`), "Access revoked");
      if (!window.confirm(`Remove ${p.name}'s People entry? Guest access is revoked (photos and any account stay).`)) return null;
      const keep = !!p.month && window.confirm(`Keep ${p.name}'s Birthday Book? OK keeps it, Cancel hides it.`);
      return run(() => api.delete(`/people/${p.id}`, { params: { keep_book: keep } }), "Removed");
    },
    edit: (row) => setEditing(row.person ? editable(row.person) : { ...EMPTY_PERSON, name: row.user.name || "", email: row.user.email, user_id: row.user.user_id, isNew: true }),
  };
  const save = (body, guest) => run(async () => {
    const { data } = !editing.id ? await api.post("/people", body) : await api.patch(`/people/${editing.id}`, body);
    if (guest) await api.post(`/people/${data.id}/guest`, guest);
    setEditing(null);
  }, guest?.send_email ? "Saved and link emailed" : "Saved");
  const rows = useMemo(() => (users ? mergeRows(users, people) : []), [users, people]);
  if (!users) return null;
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => matches(r, filter) && (!needle || [r.user?.name, r.user?.email, r.person?.name, r.person?.email].some((s) => s?.toLowerCase().includes(needle))));
  const pending = rows.filter((r) => matches(r, "pending")).length;
  return (
    <div data-testid="admin-members-tab">
      <p className="text-sm text-neutral-600 mb-4">Everyone in one place. Family members sign up themselves and you approve them here; guests get up to 7 days of view-only access.</p>
      <GuestSignupCard />
      {editing ? (
        <PersonEditor users={users} onSave={save} onCancel={() => setEditing(null)} initial={editing} chooseKind={!editing.id && !editing.user_id} />
      ) : (
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <button className="btn-primary !py-1.5 !px-4 text-sm" onClick={() => setEditing({ ...EMPTY_PERSON })} data-testid="person-add-button">Add person</button>
          <input className="login-input !mb-0 !py-1.5 max-w-xs" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="members-search" />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="members-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k} className={`name-chip ${filter === k ? "!bg-neutral-900 !text-white" : ""}`} onClick={() => setFilter(k)} data-testid={`members-filter-${k}`}>
            {l}{k === "pending" && pending > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-red-600 text-white text-[10px]" data-testid="members-pending-count">{pending}</span>}
            {k === "suspicious" && suspicious > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-red-600 text-white text-[10px]" data-testid="members-suspicious-count">{suspicious}</span>}
          </button>
        ))}
      </div>
      {filter !== "suspicious" && (
        <div className="admin-table" data-testid="members-list">
          {!shown.length && !["all", "guests"].includes(filter) && <p className="text-sm text-neutral-500" data-testid="members-empty">Nobody here.</p>}
          {shown.map((r) => <MemberRow key={r.key} row={r} me={me} ops={ops} />)}
        </div>
      )}
      {["all", "guests", "suspicious"].includes(filter) && <SelfGuests needle={needle} onlySuspicious={filter === "suspicious"} onCount={setSuspicious} />}
      <DeletionJobs onDone={load} />
      <DeletedAccounts />
    </div>
  );
};
